import { watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useRuntimeConfig, useRoute, useRouter } from 'nuxt/app'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import { log } from '@common/core-layer/services/logger.service'
import { useGlobalStore } from '@common/core-layer/stores/global.store'

import { useLocationStore } from '@common/location-layer/store/location.store'
import { useNotificationReadSync } from '@common/notification-layer/composables/useNotificationReadSync'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

import { assetsDrmRegion } from '@common/http-layer/utils/regional-host'
import { fcmServiceWorkerPath, fcmServiceWorkerRegion, isFcmServiceWorkerScriptUrl } from '../constants/fcm-service-worker'
import { fcmConfigForRegion, type FcmClientConfig, type FcmRegion } from '../utils/fcm-region.utils'
import { PushNotificationsService } from '../services/push-notifications.service'
import { resolvePushNotificationNavigation } from '../utils/push-notification-redirect.utils'
import {
  NOTIFICATION_REF_ID_QUERY_PARAM,
  clearPendingNotificationRefIdIf,
  normalizeNotificationRefId,
  readPendingNotificationRefId,
  storePendingNotificationRefId,
} from '../utils/push-notification-ref-id.utils'
import {
  FIREBASE_MESSAGING_DB_NAME,
  deleteFirebaseIndexedDbs,
  findStaleFirebaseIndexedDbs,
  isIndexedDbVersionError,
  persistFirebaseIdbRecoveryAttempt,
  readFirebaseIdbRecoveryGuard,
} from '../utils/firebase-indexeddb.utils'
import { usePushNotificationsStore } from '../store/push-notifications.store'
import {
  canAttemptAuthenticatedPushRegistration,
  isWebPushSupportedInBrowser,
  resolveSessionKeyForPush,
  shouldReuseCachedDeviceFcmToken,
  updateRegistrationStateAfterOutcome,
  type RegistrationState,
} from '../utils/push-notifications.utils'
import type { PushRegisterOutcome } from '../types/push-notifications.types'
import type { Messaging } from 'firebase/messaging'

/** Unsubscribe from the deferred `locationId` watch when the plugin disposes. */
let stopPendingLocationWatch: (() => void) | null = null

/** Unsubscribe from modular `onMessage` when plugin disposes or handler is re-registered */
let stopForegroundMessageListener: (() => void) | null = null

/**
 * One reload per page load when the sessionStorage guard is unavailable (private mode /
 * blocked storage). The persistent guard cannot be written there, so this in-memory flag
 * is what stops a purge → reload → purge cycle within a single page lifetime.
 */
let firebaseIdbReloadAttempted = false

/** Unsubscribe from the watch waiting for authentication before a notification read. */
let stopPendingReadWatch: (() => void) | null = null

function stopPendingNotificationReadWatch(): void {
  stopPendingReadWatch?.()
  stopPendingReadWatch = null
}

function stopPendingRegistrationWatch(): void {
  stopPendingLocationWatch?.()
  stopPendingLocationWatch = null
}

/** Clear deferred registration state on logout (preserves SW bridge + foreground handler). */
export function cleanupPushNotificationsOnLogout(): void {
  usePushNotificationsStore().reset()
  stopPendingRegistrationWatch()
  stopPendingNotificationReadWatch()
}

function stopForegroundListener(): void {
  stopForegroundMessageListener?.()
  stopForegroundMessageListener = null
}

/**
 * Composable for Firebase Cloud Messaging push notifications.
 *
 * Ported from @ghl/clientportal-core with these responsibilities:
 *  - Request browser notification permission
 *  - Obtain an FCM token via Firebase Messaging v9+ SDK
 *  - Register the token with the backend
 *  - Handle foreground messages (show system notification via SW)
 *  - Bridge SW → app: listen for `redirect-from-notificationclick` messages
 *    and navigate the app (push payloads are separate from in-app notifications)
 */
export function usePushNotifications() {
  const runtimeConfig = useRuntimeConfig()
  const router = useRouter()
  const { CPNavigateTo } = useCPNavigation()
  const globalStore = useGlobalStore()
  const pushStore = usePushNotificationsStore()
  const { markNotificationRefIdAsRead } = useNotificationReadSync()

  /** Issues the read and drops the persisted copy once it lands. */
  async function _completeNotificationRead(refId: string): Promise<void> {
    if (await markNotificationRefIdAsRead(refId)) {
      // Only clears when the stored value is still this refId — a newer click may have
      // replaced it while this read was in flight, and that one still needs its retry.
      clearPendingNotificationRefIdIf(refId)
    }
  }

  /**
   * Runs the pending read as soon as the session is restored. A cold start (and a
   * click that arrives mid-boot) can reach this before auth settles, and the read
   * API would reject an anonymous request.
   */
  function _scheduleNotificationReadOnLogin(authStore: ReturnType<typeof useAuthStore>): void {
    if (stopPendingReadWatch) return

    stopPendingReadWatch = watch(
      () => authStore.isLoggedIn,
      loggedIn => {
        if (!loggedIn) return
        stopPendingNotificationReadWatch()

        const pendingRefId = readPendingNotificationRefId()
        if (pendingRefId) void _completeNotificationRead(pendingRefId)
      },
      { flush: 'post' }
    )
  }

  /**
   * Marks the tapped notification read without blocking navigation — the read
   * API is best-effort, a failure must never swallow the redirect.
   * Mirrors clientportal-core's `initialize.ts` push-click behaviour.
   *
   * The refId is persisted before the read so an auth redirect or a full reload
   * cannot lose it; it is cleared once the read succeeds.
   */
  function markClickedNotificationAsRead(refId: string | null): void {
    if (!refId) return

    const authStore = useAuthStore()
    storePendingNotificationRefId(refId)

    if (authStore.isLoggedIn) {
      void _completeNotificationRead(refId)
      return
    }

    _scheduleNotificationReadOnLogin(authStore)
  }

  /**
   * True when the session region's Firebase config (`runtimeConfig.public.firebaseMessaging` for US,
   * `firebaseMessagingAu` for AU) has non-empty `apiKey` and `appId` (US values are supplied at
   * runtime via `NUXT_PUBLIC_FIREBASE_MESSAGING_API_KEY` / `NUXT_PUBLIC_FIREBASE_MESSAGING_APP_ID`).
   * If either is missing, FCM must not run — avoids permission prompts and Firebase init errors.
   */
  function isFirebaseMessagingConfigured(): boolean {
    const { apiKey, appId } = fcmConfig()
    return Boolean(String(apiKey ?? '').trim() && String(appId ?? '').trim())
  }

  /** AU sessions push through the AU Firebase project. Pod region, then token, then host; unknown is US. */
  function fcmRegion(): FcmRegion {
    return assetsDrmRegion(useAuthStore().token) ?? 'us'
  }

  function fcmConfig(region: FcmRegion = fcmRegion()): FcmClientConfig {
    return fcmConfigForRegion(runtimeConfig.public, region)
  }

  // ── Firebase Messaging (lazy, singleton) ──────────────────────────────────
  let _messagingInstance: Messaging | null = null
  let _messagingRegion: FcmRegion | null = null

  /**
   * Legacy Firebase SDKs (ghl-revex-frontend / clientportal-core) left IndexedDB
   * databases at a higher version than `firebase@12` opens them with, which makes
   * every `getToken()` fail with `VersionError` for the life of that browser
   * profile. Drop the stale databases before the SDK touches them.
   */
  async function _purgeStaleFirebaseIndexedDbs(): Promise<void> {
    if (typeof indexedDB === 'undefined') return

    const stale = await findStaleFirebaseIndexedDbs(indexedDB)
    if (!stale.length) return

    const purged = await deleteFirebaseIndexedDbs(indexedDB, stale)
    log.warn('[FCM] Removed stale Firebase IndexedDB databases', { labels: { databases: purged.join(', ') } })
  }

  /** Session storage, or undefined when the context blocks access to it. */
  function _recoveryGuardStorage(): Storage | undefined {
    try {
      return window.sessionStorage ?? undefined
    } catch (err) {
      log.warn('[FCM] IndexedDB recovery guard unavailable — falling back to an in-memory guard', {
        error: err instanceof Error ? err : undefined,
      })
      return undefined
    }
  }

  /**
   * Without persistent storage the in-memory flag is the only thing stopping a
   * purge → reload → purge cycle inside one page lifetime.
   */
  function _allowRecoveryReloadWithoutGuard(): boolean {
    if (firebaseIdbReloadAttempted) {
      log.warn('[FCM] Skipping a second IndexedDB recovery reload without persistent storage')
      return false
    }

    firebaseIdbReloadAttempted = true
    return true
  }

  /**
   * Last-resort recovery for browsers without `indexedDB.databases()` (Firefox):
   * the stale database can only be detected by the failure itself. Firebase caches
   * the rejected `open` promise for the page's lifetime, so the databases are dropped
   * and the page reloaded once. Reloading is safe only after a confirmed purge — the
   * next load then finds no stale database.
   */
  async function _recoverFromFirebaseIdbVersionError(): Promise<void> {
    if (typeof indexedDB === 'undefined' || typeof window === 'undefined') return

    const storage = _recoveryGuardStorage()
    if (readFirebaseIdbRecoveryGuard(storage) === 'attempted') return

    const purged = await deleteFirebaseIndexedDbs(indexedDB)

    // A delete reports `blocked` while another tab holds the database open. Reloading then
    // would burn the one-shot guard on a purge that never happened, leaving the tab stuck on
    // the same VersionError. Leave the guard unset so the next attempt can retry.
    if (!purged.includes(FIREBASE_MESSAGING_DB_NAME)) {
      log.warn('[FCM] IndexedDB purge did not complete (database in use by another tab) — leaving recovery retryable')
      return
    }

    if (!persistFirebaseIdbRecoveryAttempt(storage) && !_allowRecoveryReloadWithoutGuard()) return

    log.warn('[FCM] Recovered from IndexedDB VersionError, reloading', { labels: { databases: purged.join(', ') } })
    window.location.reload()
  }

  async function _getMessaging(): Promise<Messaging | null> {
    if (!isFirebaseMessagingConfigured()) return null
    const region = fcmRegion()
    if (_messagingInstance && _messagingRegion === region) return _messagingInstance

    try {
      await _purgeStaleFirebaseIndexedDbs()

      const { initializeApp, getApps } = await import('firebase/app')
      const { getInstallations } = await import('firebase/installations')
      const { getMessaging } = await import('firebase/messaging')

      const { apiKey, authDomain, projectId, messagingSenderId, appId } = fcmConfig(region)

      // One named app per region so the US and AU projects never share an instance.
      const appName = `cp-fcm-${region}`
      const firebaseApp =
        getApps().find(app => app.name === appName) ?? initializeApp({ apiKey, authDomain, projectId, messagingSenderId, appId }, appName)

      // Ensure Installations gets registered before Messaging asks for
      // `installations-internal`; this avoids runtime errors in some optimized bundles.
      getInstallations(firebaseApp)
      _messagingInstance = getMessaging(firebaseApp)
      _messagingRegion = region
      return _messagingInstance
    } catch (err) {
      console.error('[FCM] Failed to initialise Firebase Messaging:', err)
      return null
    }
  }

  function _fcmServiceWorkerRegistration(reg: ServiceWorkerRegistration): boolean {
    const urls = [reg.active?.scriptURL, reg.waiting?.scriptURL, reg.installing?.scriptURL]
    return urls.some(isFcmServiceWorkerScriptUrl)
  }

  function _findFcmServiceWorkerRegistration(registrations: readonly ServiceWorkerRegistration[]): ServiceWorkerRegistration | undefined {
    return registrations.find(_fcmServiceWorkerRegistration)
  }

  /** Reuses the worker only when it embeds this region's Firebase project; otherwise re-registers at `/`. */
  async function _ensureFcmServiceWorkerRegistration(region: FcmRegion = fcmRegion()): Promise<ServiceWorkerRegistration | undefined> {
    const registrations = await navigator.serviceWorker.getRegistrations()
    const existing = _findFcmServiceWorkerRegistration(registrations)
    const existingUrl = existing?.active?.scriptURL ?? existing?.waiting?.scriptURL ?? existing?.installing?.scriptURL
    if (existing && fcmServiceWorkerRegion(existingUrl) === region) return existing

    try {
      const registration = await navigator.serviceWorker.register(fcmServiceWorkerPath(region), { scope: '/' })
      await navigator.serviceWorker.ready
      return registration
    } catch (err) {
      console.error('[FCM] Failed to register FCM service worker:', err)
      return undefined
    }
  }

  function ensurePendingRegistrationWatch(): void {
    if (stopPendingLocationWatch) return
    const locationStore = useLocationStore()
    const { locationId } = storeToRefs(locationStore)
    stopPendingLocationWatch = watch(
      locationId,
      async id => {
        const token = pushStore.pendingFcmToken
        if (!String(id ?? '').trim() || !token) return
        pushStore.setPendingFcmToken(null)
        await _registerPushToken(token)
      },
      { flush: 'post' }
    )
  }

  // ── Token → backend ───────────────────────────────────────────────────────
  async function _registerPushToken(token: string): Promise<PushRegisterOutcome> {
    try {
      const authStore = useAuthStore()
      if (!authStore.isLoggedIn) {
        console.warn('[FCM] User not authenticated — skipping push token registration')
        return 'skipped'
      }

      const sessionId = resolveSessionKeyForPush(authStore)
      if (!sessionId) {
        console.warn('[FCM] sessionId not available — skipping push token registration')
        return 'skipped'
      }

      const locationStore = useLocationStore()
      const locationId = locationStore.locationId

      if (!String(locationId ?? '').trim()) {
        console.warn('[FCM] locationId not available — deferring push token registration until location is ready')
        pushStore.setPendingFcmToken(token)
        ensurePendingRegistrationWatch()
        return 'deferred'
      }

      const service = new PushNotificationsService()
      // GoKollab web uses the standard register-push-token path (highlevel-staging FCM
      // validation + delivery). register-push-token-gokollab is for the native Flutter
      // app (v7+) and validates against the GoKollab native FCM service account.
      const appType = globalStore.isGokollab ? 'GoKollab' : 'ClientPortal'
      await service.registerPushToken({ pushToken: token, sessionId, deviceType: 'web', appType }, locationId)

      pushStore.setPendingFcmToken(null)
      stopPendingRegistrationWatch()
      return 'success'
    } catch (err) {
      console.error('[FCM] Push token registration failed:', err)
      return 'failed'
    }
  }

  /**
   * Never call `getToken` without a registration: passing `undefined` makes the SDK
   * fall back to registering its default `/firebase-messaging-sw.js`, which this app
   * does not serve and which sw-cleanup unregisters as a legacy worker. That fallback
   * leaves `messaging.swRegistration` unusable and surfaces as an uncaught
   * `TypeError: Cannot read properties of undefined (reading 'pushManager')`.
   */
  async function obtainDeviceFcmToken(messaging: Messaging): Promise<string | null> {
    const region = fcmRegion()
    const swReg = await _ensureFcmServiceWorkerRegistration(region)
    if (!swReg) {
      log.warn('[FCM] FCM service worker unavailable — skipping token request')
      return null
    }

    const { getToken } = await import('firebase/messaging')
    const vapidKey = fcmConfig(region).webPushCertificate || undefined
    const token = await getToken(messaging, {
      ...(vapidKey ? { vapidKey } : {}),
      serviceWorkerRegistration: swReg,
    })
    return token || null
  }

  // ── Permission + token registration ───────────────────────────────────────
  /**
   * Request notification permission, obtain the FCM token and register it
   * with the backend.  Skips silently on WebKit (Safari) which does not
   * support the Web Push standard.
   */
  async function requestAndRegisterPushToken(): Promise<PushRegisterOutcome> {
    if (!isFirebaseMessagingConfigured()) return 'skipped'
    if (!isWebPushSupportedInBrowser()) return 'skipped'

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return 'skipped'

    const messaging = await _getMessaging()
    if (!messaging) return 'skipped'

    try {
      const token = await obtainDeviceFcmToken(messaging)
      if (!token) return 'skipped'

      pushStore.setCachedDeviceFcmToken(token)
      return await _registerPushToken(token)
    } catch (err) {
      console.error('[FCM] Failed to obtain FCM token:', err)
      if (isIndexedDbVersionError(err)) {
        await _recoverFromFirebaseIdbVersionError()
      }
      return 'failed'
    }
  }

  // ── Foreground message handler (app is open) ───────────────────────────────
  /**
   * When the app is in the foreground Firebase does not show a system
   * notification automatically.  We display it manually via the active SW
   * so behaviour is consistent with background messages.
   */
  async function setupForegroundMessageHandler(): Promise<void> {
    if (!isFirebaseMessagingConfigured()) return

    const messaging = await _getMessaging()
    if (!messaging) return

    stopForegroundListener()

    const { onMessage } = await import('firebase/messaging')

    stopForegroundMessageListener = onMessage(messaging, async payload => {
      const title = payload.notification?.title ?? ''
      const options: NotificationOptions = {
        body: payload.notification?.body,
        data: payload.data,
      }

      const swReg = await _ensureFcmServiceWorkerRegistration()
      if (swReg) {
        await swReg.showNotification(title, options)
      }
    })
  }

  // ── SW → app bridge (notification click → in-app route) ────────────────────
  /**
   * Registers a listener for `redirect-from-notificationclick` messages posted
   * by the cp-firebase-messaging-sw service worker and navigates the SPA.
   *
   * Returns a cleanup function that removes the event listener — call it
   * from plugin teardown if needed.
   *
   * Conversation redirect paths resolve to the `communities-conversations-conversationId`
   * route; the `conversation-deep-link` middleware + client plugin (communities routing-layer)
   * then decide desktop floating window vs mobile route. This bridge stays domain-free.
   */
  function setupSWMessageListener(): () => void {
    if (!isFirebaseMessagingConfigured()) return () => {}
    if (!('serviceWorker' in navigator)) return () => {}

    async function onSWMessage(event: MessageEvent): Promise<void> {
      if (event.data?.action !== 'redirect-from-notificationclick') return

      const raw = event.data?.redirectPath
      const path = typeof raw === 'string' ? raw.trim() : ''
      const rawRefId = event.data?.refId
      const refId = typeof rawRefId === 'string' ? rawRefId : undefined

      markClickedNotificationAsRead(normalizeNotificationRefId(refId))

      const resolvedNavigation = resolvePushNotificationNavigation(path, { refId })
      if (resolvedNavigation) {
        await CPNavigateTo(resolvedNavigation)
        return
      }

      if (!path) {
        if (raw != null && raw !== '') console.warn('[FCM] Ignoring redirectPath — expected non-empty string:', raw)
        return
      }

      // Unmapped legacy paths still use raw router.push until resolver coverage expands.
      // Leading slashes are collapsed so a payload like `//attacker.example/phish` cannot be
      // read as a protocol-relative URL.
      const target = `/${path.replace(/^\/+/, '')}`
      await router.push(target)
    }

    navigator.serviceWorker.addEventListener('message', onSWMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', onSWMessage)
    }
  }

  /** Stops deferred `locationId` watch and foreground `onMessage` listener (e.g. plugin HMR / teardown). */
  function disposePushNotificationSideEffects(): void {
    stopPendingRegistrationWatch()
    stopPendingNotificationReadWatch()
    stopForegroundListener()
    firebaseIdbReloadAttempted = false
    _messagingInstance = null
    _messagingRegion = null
  }

  /**
   * Register push token when the user is authenticated, and watch for post-login session.
   * Returns cleanup for plugin teardown / HMR.
   */
  function setupAuthenticatedPushRegistration(): () => void {
    const authStore = useAuthStore()
    const locationStore = useLocationStore()
    const registrationState: RegistrationState = {
      registeredForCurrentSession: false,
      lastRegisteredLocationId: '',
    }

    const attemptRegister = async (): Promise<void> => {
      const sessionKey = resolveSessionKeyForPush(authStore) ?? ''
      const loc = String(locationStore.locationId ?? '').trim()

      if (
        !canAttemptAuthenticatedPushRegistration({
          loggedIn: authStore.isLoggedIn,
          sessionKey,
          locationId: loc,
          registeredForCurrentSession: registrationState.registeredForCurrentSession,
          lastRegisteredLocationId: registrationState.lastRegisteredLocationId,
        })
      ) {
        return
      }

      const cachedToken = pushStore.cachedDeviceFcmToken
      const useCachedToken = shouldReuseCachedDeviceFcmToken(cachedToken, loc, registrationState.lastRegisteredLocationId)
      registrationState.registeredForCurrentSession = true

      const outcome = useCachedToken && cachedToken ? await _registerPushToken(cachedToken) : await requestAndRegisterPushToken()

      updateRegistrationStateAfterOutcome(outcome, loc, registrationState)
    }

    const stopAuthWatch = watch(
      () => ({
        loggedIn: authStore.isLoggedIn,
        sessionKey: resolveSessionKeyForPush(authStore) ?? '',
        loc: String(locationStore.locationId ?? '').trim(),
      }),
      ({ loggedIn, sessionKey, loc }) => {
        if (!loggedIn || !sessionKey) {
          registrationState.registeredForCurrentSession = false
          registrationState.lastRegisteredLocationId = ''
          cleanupPushNotificationsOnLogout()
          return
        }

        if (loc && registrationState.lastRegisteredLocationId && loc !== registrationState.lastRegisteredLocationId) {
          registrationState.registeredForCurrentSession = false
        }

        void attemptRegister()
      },
      { flush: 'post', immediate: true }
    )

    return () => {
      stopAuthWatch()
      registrationState.registeredForCurrentSession = false
      registrationState.lastRegisteredLocationId = ''
    }
  }

  // ── Cold start (no window client was open when the notification was clicked) ─
  /**
   * Reads the `notificationRefId` query param the service worker appended to the
   * opened URL, marks that notification read and strips the param so it does not
   * leak into shared links, history or re-triggers on refresh.
   */
  async function consumeColdStartNotificationRefId(): Promise<void> {
    const route = useRoute()
    const refId = normalizeNotificationRefId(route.query?.[NOTIFICATION_REF_ID_QUERY_PARAM])

    // No param: a previous cold start may still have a read waiting for authentication,
    // e.g. when login middleware reloaded the page before the session was restored.
    if (!refId) {
      const pendingRefId = readPendingNotificationRefId()
      if (pendingRefId) markClickedNotificationAsRead(pendingRefId)
      return
    }

    // Persist before stripping: the param must not survive a refresh, a shared link or the
    // login redirect, but the refId itself has to outlive them.
    storePendingNotificationRefId(refId)

    // Dispatch the read before the route cleanup: a cancelled or rejected `replace` must not
    // swallow the acknowledgement, and on an authenticated page there is no watcher to retry it.
    markClickedNotificationAsRead(refId)

    const { [NOTIFICATION_REF_ID_QUERY_PARAM]: _consumed, ...remainingQuery } = route.query
    await router.replace({ path: route.path, query: remainingQuery, hash: route.hash })
  }

  return {
    isFirebaseMessagingConfigured,
    consumeColdStartNotificationRefId,
    requestAndRegisterPushToken,
    setupForegroundMessageHandler,
    setupSWMessageListener,
    setupAuthenticatedPushRegistration,
    disposePushNotificationSideEffects,
  }
}
